import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router-dom";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    RoleBreadcrumbs,
    RoleCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as RoleActions from "../../../actions/role";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { LookupsLayout } from "../../index";

import "./RolePage.scss";

interface RolePageProps {
    form: Dtos.Role & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    role: Dtos.Role;
    lookups: Dtos.Lookup[];
    loadingRole: boolean;
    loadRoleFailure: boolean;
    loadRoleErrorCode: string;
    savingRole: boolean;
    saveRoleSuccess: boolean;
    saveRoleFailure: boolean;
    creatingRole: boolean;
    createRoleSuccess: boolean;
    validatingRole: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearRole: RoleActions.IRoleClearActionCreator;
    loadRole: RoleActions.IRoleLoadByIdActionCreator;
    saveRole: RoleActions.IRoleSaveActionCreator;
    createRole: RoleActions.IRoleCreateActionCreator;
    validateRole: RoleActions.IRoleValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.role";

const RolePage: FC<RolePageProps> = ({
    form,
    formState,
    formProperties,
    role,
    lookups,
    loadingRole,
    loadRoleFailure,
    loadRoleErrorCode,
    savingRole,
    saveRoleSuccess,
    saveRoleFailure,
    creatingRole,
    createRoleSuccess,
    validatingRole,
    validationFailures,
    permissions,
    clearRole,
    loadRole,
    saveRole,
    createRole,
    validateRole,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { roleId } = useParams<{ roleId: string }>();
    const roleIdNumber = parseInt(roleId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.Role = {} as Dtos.Role;

        loadForm(reduxFormName, Object.assign({ ...role }, extraData));
    };


    // Load role
    useEffect(() => {
        if (roleIdNumber && !role && !loadingRole) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(RoleBreadcrumbs(roleIdNumber));
            loadRole(roleIdNumber);
        } else if (!roleIdNumber && !creatingRole && !createRoleSuccess) {
            createRole();
            setBreadcrumbs(RoleCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (role && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        roleIdNumber,
        role,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        RoleBreadcrumbs,
        loadRole,
        createRole,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingRole,
        creatingRole,
        createRoleSuccess,
        RoleCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadRoleFailure) {
            navigate(`/error/${loadRoleErrorCode}`);
        }
    }, [loadRoleFailure, loadRoleErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (roleIdNumber && role?.value) {
            setBreadcrumbs(RoleBreadcrumbs(roleIdNumber, role?.value));
        }
    }, [roleIdNumber, role, setBreadcrumbs, RoleBreadcrumbs]);

    // Reload role after saving
    useEffect(() => {
        if (!savingRole && saveRoleSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Role saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/general/roles";
                navigate(url);
            } else if (!roleIdNumber) {
                let url: string = `/settings/general/roles/${role?.id}`;
                navigate(url);
                clearRole();
            }
        }
    }, [
        savingRole,
        saveRoleSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingRole && saveRoleFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingRole, saveRoleFailure]);

    // Cleanup effect to clear the role on component unmount
    useEffect(() => {
        return () => {
            clearRole();
            resetForm(reduxFormName);
        };
    }, [clearRole, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/general/roles";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.Role) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.Role = {} as Dtos.Role;

            await saveRole(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.Role = {} as Dtos.Role;

            if (form && hasBeenSubmitted && !validatingRole) {
                await validateRole(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.RolesUpdate,
                Dtos.Permission.RolesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingRole ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);
        const formDisabledSystem: boolean =
            formDisabled || (form && form.isSystem);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "role",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled:
                                                    formDisabledSystem,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "role",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled:
                                                    formDisabledSystem,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "role",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled:
                                                    formDisabledSystem,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            permissionsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Permissions</h1>
                        <div
                            className="card card-shadow scrolly customscrollbar"
                            style={{ zIndex: 1 }}
                        >
                            <div className="form-row py-4 px-4 overflow-scroll">
                                <div className="col-md-12">
                                    <p>
                                        <h5>
                                            <em>
                                                Contact NewtonGreen if changes
                                                are required
                                            </em>
                                        </h5>
                                    </p>
                                    <hr />
                                    {form.groupedRolePermissions &&
                                        form.groupedRolePermissions.map(
                                            (value, index) => (
                                                <div
                                                    className="labels"
                                                    key={`${value}-${index}`}
                                                >
                                                    <p className="">
                                                        <strong>
                                                            {
                                                                value.moduleDescription
                                                            }
                                                        </strong>
                                                    </p>
                                                    {value.permissions &&
                                                        value.permissions.map(
                                                            (
                                                                permission,
                                                                index
                                                            ) => (
                                                                <p
                                                                    key={`${permission}-${index}`}
                                                                    className="pl-4"
                                                                >
                                                                    {
                                                                        permission.permissionDescription
                                                                    }
                                                                    {permission.institutionMatch && (
                                                                        <span className="float-right badge badge-info">
                                                                            Assigned
                                                                            Institution
                                                                        </span>
                                                                    )}
                                                                </p>
                                                            )
                                                        )}
                                                </div>
                                            )
                                        )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "role",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingRole,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <LookupsLayout
            loading={loadingRole}
            permission={Dtos.Permission.RolesView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<RolePageProps> => {
    let role: Dtos.Role | undefined = !(state.roles.formData instanceof Array)
        ? state.roles.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        role: role!,
        loadingRole:
            state.roles.loadState &&
            state.roles.loadState.status == RequestState.Pending,
        loadRoleFailure:
            state.roles.loadState &&
            state.roles.loadState.status == RequestState.Failure,
        loadRoleErrorCode: state.roles.loadState.errorCode,
        savingRole:
            state.roles.saveState &&
            state.roles.saveState.status == RequestState.Pending,
        saveRoleSuccess:
            state.roles.saveState &&
            state.roles.saveState.status == RequestState.Success,
        saveRoleFailure:
            state.roles.saveState &&
            state.roles.saveState.status == RequestState.Failure,
        creatingRole:
            state.roles.createState &&
            state.roles.createState.status == RequestState.Pending,
        createRoleSuccess:
            state.roles.createState &&
            state.roles.createState.status == RequestState.Success,
        validatingRole:
            state.roles.validateState &&
            state.roles.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.roles.lookups,
        form: state.reduxForms.role,
        formState: state.roles.formState,
        formProperties: state.roles.formProperties,
        validationFailures: role ? state.roles.validationFailures : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<RolePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadRole: bindActionCreators(RoleActions.LoadRoleById, dispatch),
        clearRole: bindActionCreators(RoleActions.Clear, dispatch),
        saveRole: bindActionCreators(RoleActions.SaveRole, dispatch),
        createRole: bindActionCreators(RoleActions.CreateRole, dispatch),
        validateRole: bindActionCreators(RoleActions.ValidateRole, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RolePage);
